// La Luna (posizione reale) e le lune principali dei pianeti. Testi e nomi nei dizionari (t().lune).
// Luna: teoria semplificata di Meeus («Astronomical Algorithms», cap. 47, termini principali): circa 0,3° e qualche
// migliaio di km di errore, più che sufficiente a vista. Le altre lune: orbite circolari medie nel piano equatoriale
// del pianeta; le lunghezze iniziali dei satelliti galileiani sono quelle medie di Meeus (cap. 44), per Titano,
// Phobos e Deimos la posizione sull'orbita è indicativa (dichiarato nella scheda).

export interface Luna {
  id: string;
  pianeta: string;
  raggioKm: number;
  semiasseKm: number;
  periodoGiorni: number;
  /** Longitudine media a J2000 (gradi). */
  l0: number;
  /** Distanza nella scena del sistema solare, in raggi (della scena) del pianeta: le distanze vere non starebbero. */
  distanzaScena: number;
  colore: number;
  texture?: string;
}

export const LUNE: Luna[] = [
  { id: 'luna', pianeta: 'terra', raggioKm: 1737.4, semiasseKm: 384_400, periodoGiorni: 27.321661, l0: 0, distanzaScena: 2.7, colore: 0xffffff, texture: 'luna' },
  { id: 'phobos', pianeta: 'marte', raggioKm: 11.3, semiasseKm: 9376, periodoGiorni: 0.31891, l0: 0, distanzaScena: 1.8, colore: 0x7d6e60 },
  { id: 'deimos', pianeta: 'marte', raggioKm: 6.2, semiasseKm: 23_463, periodoGiorni: 1.26244, l0: 120, distanzaScena: 2.6, colore: 0x93836f },
  { id: 'io', pianeta: 'giove', raggioKm: 1821.6, semiasseKm: 421_700, periodoGiorni: 1.769138, l0: 106.07719, distanzaScena: 2.0, colore: 0xd9c26a },
  { id: 'europa', pianeta: 'giove', raggioKm: 1560.8, semiasseKm: 671_034, periodoGiorni: 3.551181, l0: 175.73161, distanzaScena: 2.6, colore: 0xd3c8b4 },
  { id: 'ganimede', pianeta: 'giove', raggioKm: 2634.1, semiasseKm: 1_070_412, periodoGiorni: 7.154553, l0: 120.55883, distanzaScena: 3.3, colore: 0x9a8e80 },
  { id: 'callisto', pianeta: 'giove', raggioKm: 2410.3, semiasseKm: 1_882_709, periodoGiorni: 16.689018, l0: 84.44459, distanzaScena: 4.3, colore: 0x6f665d },
  { id: 'titano', pianeta: 'saturno', raggioKm: 2574.7, semiasseKm: 1_221_870, periodoGiorni: 15.945421, l0: 0, distanzaScena: 3.0, colore: 0xd6a24e },
];

const rad = (g: number) => (g * Math.PI) / 180;
const norm = (g: number) => ((g % 360) + 360) % 360;

/** Angolo (radianti) di una luna sulla sua orbita circolare alla data giuliana jd. */
export function angoloLuna(l: Luna, jd: number): number {
  return rad(l.l0 + (360 / l.periodoGiorni) * (jd - 2451545.0));
}

export interface PosizioneLuna {
  /** Geocentrica, equatoriale (km): x verso l'equinozio, z verso il polo nord. */
  equatoriale: [number, number, number];
  /** Geocentrica, eclittica (km). */
  eclittica: [number, number, number];
  distanzaKm: number;
  /** Frazione del disco illuminata (0-1) e fase (0 nuova … 4 piena … 7), dalla elongazione dal Sole. */
  illuminata: number;
  fase: number;
}

export function posizioneLuna(jd: number): PosizioneLuna {
  const T = (jd - 2451545.0) / 36525;
  const L = norm(218.3164477 + 481267.88123421 * T);
  const D = rad(norm(297.8501921 + 445267.1114034 * T));
  const M = rad(norm(357.5291092 + 35999.0502909 * T));
  const Mp = rad(norm(134.9633964 + 477198.8675055 * T));
  const F = rad(norm(93.272095 + 483202.0175233 * T));
  const lon = rad(L + 6.289 * Math.sin(Mp) + 1.274 * Math.sin(2 * D - Mp) + 0.658 * Math.sin(2 * D)
    + 0.214 * Math.sin(2 * Mp) - 0.186 * Math.sin(M) - 0.114 * Math.sin(2 * F));
  const lat = rad(5.128 * Math.sin(F) + 0.281 * Math.sin(Mp + F) + 0.278 * Math.sin(Mp - F) + 0.173 * Math.sin(2 * D - F));
  const dist = 385_001 - 20_905 * Math.cos(Mp) - 3699 * Math.cos(2 * D - Mp) - 2956 * Math.cos(2 * D) - 570 * Math.cos(2 * Mp);
  const ecl: [number, number, number] = [
    dist * Math.cos(lat) * Math.cos(lon),
    dist * Math.cos(lat) * Math.sin(lon),
    dist * Math.sin(lat),
  ];
  const eps = rad(23.4393 - 0.013 * T);
  const equ: [number, number, number] = [
    ecl[0],
    ecl[1] * Math.cos(eps) - ecl[2] * Math.sin(eps),
    ecl[1] * Math.sin(eps) + ecl[2] * Math.cos(eps),
  ];
  // Elongazione dal Sole (longitudine del Sole di Meeus, cap. 25, bassa precisione).
  const lonSole = norm(280.46646 + 36000.76983 * T + 1.9146 * Math.sin(M));
  const elong = norm((lon * 180) / Math.PI - lonSole);
  return {
    equatoriale: equ,
    eclittica: ecl,
    distanzaKm: dist,
    illuminata: (1 - Math.cos(rad(elong))) / 2,
    fase: Math.round(elong / 45) % 8,
  };
}
